import React, { ComponentType } from "react";
import { CustomField, UserData, ZomentumWindow } from "@/Contracts";
import {
	Action,
	applyMiddleware,
	compose,
	createStore,
	Middleware,
	Reducer,
	Store
} from "redux";
import ReduxLogger from "redux-logger";
import ReduxThunk, { ThunkDispatch } from "redux-thunk";
import { Provider, createDispatchHook, createSelectorHook } from "react-redux";
import isPlainObject from "lodash/isPlainObject";

declare let window: ZomentumWindow;

// mapping of id <> key_to_hydrate
const hydrationMapping: Record<string, string[]> = {
	user_id: ["user", "creator_user"],
	owner_user_id: ["owner_user"],
	collaborator_ids: ["collaborators"],
	author_id: ["author"],
	owner_id: ["author"],
	rejected_by_user_id: ["rejected_by_user"],
	approved_by_user_id: ["approved_by_user"]
};

const userObjects = Object.values(hydrationMapping).flat();

/**
 *
 * @param userList Company user list of logged in user
 * @param userId Referenced user of the entity: user_id | creator_user_id | owner_user_id
 * @returns UserData | null
 */

const findAndPopulateUser = (
	userList: UserData[],
	userId: string
): UserData | null => {
	const user = userList?.find(user => user.id === userId);
	return user ?? null;
};

/**
 *
 * @param userList Company user list of logged in user
 * @param objectToHydrate Entity object to hydrate user data
 * @returns Hydrated Entity object
 */

export const hydrateUserData = (
	userList: UserData[],
	objectToHydrate: Record<string, any>
): any => {
	let hydrationObject = objectToHydrate ? { ...objectToHydrate } : null;

	Object.entries(hydrationObject ?? {}).forEach(([key, value]) => {
		if (userObjects.includes(key)) {
			// No need to hydrate hydration keys such as, user, collaborators, owner_user, etc.
			return;
		}

		const hydrationObjectKeys = hydrationMapping[key];

		hydrationObject =
			hydrationObjectKeys?.reduce(
				(prev, hydrationObjectKey) => {
					if (prev[hydrationObjectKey] === null) {
						// get the user reference id from the entity to help pick right user
						const userId = objectToHydrate[key];
						const response = findAndPopulateUser(userList, userId);

						const hydrationKey = prev[key];

						// if array of ids to hydrate, such as collaborators
						if (Array.isArray(hydrationKey)) {
							const hydratedArray = hydrationKey.map(userId =>
								findAndPopulateUser(userList, userId)
							);

							// check if the keyToHydrate is present in the entity to hydrate
							// if collaborator_id is there but no collaborators, then skip as there is nothing to hydrate
							if (
								Object.prototype.hasOwnProperty.call(
									prev,
									hydrationObjectKey
								)
							) {
								prev[hydrationObjectKey] = [...hydratedArray];
								prev.collaborators = hydratedArray;
							}
						}

						// check if the keyToHydrate is present in the entity to hydrate
						// if user_id is there but no "user", then skip as there is nothing to hydrate
						if (
							Object.prototype.hasOwnProperty.call(
								prev,
								hydrationObjectKey
							)
						) {
							prev[hydrationObjectKey] = response;
						}
					}
					return prev;
				},
				{ ...hydrationObject }
			) ?? hydrationObject;

		// Going through array in the object to scan for user properties
		// to cover user objects nested inside objects of array
		if (Array.isArray(value)) {
			const hydratedArray = value.map(data => {
				const hydratedObj = isPlainObject(data)
					? hydrateUserData(userList, data)
					: data;
				return hydratedObj;
			});

			if (isPlainObject(value)) {
				hydrationObject[key] = value;
				return;
			}

			hydrationObject[key] = [...hydratedArray];

			return;
		}

		// Recursively check of more nested hydrations needs
		if (isPlainObject(value)) {
			const innerObject = hydrateUserData(userList, value);
			hydrationObject[key] = innerObject;
		} else {
			hydrationObject[key] = value;
		}
	});

	return hydrationObject;
};

export const hydrateCustomFieldsData = (
	objectToHydrate: Record<string, any>,
	customFieldIdMappingValues: Record<string, CustomField>
): any => {
	const hydratedObject = objectToHydrate ? { ...objectToHydrate } : {};

	Object.entries(hydratedObject ?? {}).forEach(([key, value]) => {
		if (key === "custom_fields") {
			if (Array.isArray(value)) {
				const hydratedCustomFieldsArray = value.map(
					currentCustomField => {
						const hydratedCurrentCustomField =
							customFieldIdMappingValues[currentCustomField.id];
						const currentCustomFieldValues =
							currentCustomField.values;

						if (
							!hydratedCurrentCustomField &&
							currentCustomField.id
						) {
							return currentCustomField;
						}
						return {
							...hydratedCurrentCustomField,
							values: currentCustomFieldValues
						};
					}
				);
				hydratedObject[key] = hydratedCustomFieldsArray;
			}
			return;
		}

		if (Array.isArray(value)) {
			const hydratedArray = value.map(data => {
				const hydratedObj = isPlainObject(data)
					? hydrateCustomFieldsData(data, customFieldIdMappingValues)
					: data;
				return hydratedObj;
			});
			hydratedObject[key] = [...hydratedArray];
			return;
		}

		// Recursively check of more nested hydrations needs
		if (isPlainObject(value)) {
			const innerObject = hydrateCustomFieldsData(
				value,
				customFieldIdMappingValues
			);
			hydratedObject[key] = innerObject;
		} else {
			hydratedObject[key] = value;
		}
	});

	return hydratedObject;
};

export const hydrateFieldsMiddleware: Middleware =
	store => next => (action: Action) => {
		const { type, ...payload } = action;

		const prevStore = store.getState();

		const { user } = prevStore;

		const { companyUserList } = user;

		const hydratedUserEntity = hydrateUserData(companyUserList, payload);
		const { customFields } = prevStore;

		const hydratedCustomFieldEntity = hydrateCustomFieldsData(
			hydratedUserEntity,
			customFields.customFieldIdMapping
		);

		return next({
			type,
			...hydratedCustomFieldEntity
		});

		// if anythihng breaks, uncomment below code.
		// return next({
		// 	type,
		// 	...payload
		// });
	};

const middleWare: Array<Middleware> = [ReduxThunk];
let composeEnhancers = compose;

if (
	process.env.REACT_APP_NODE_ENV !== "production" &&
	process.env.NODE_ENV !== "test"
) {
	if (JSON.parse(process.env.REACT_APP_ENABLE_REDUX_LOGGER || "true")) {
		middleWare.push(ReduxLogger);
	}
	const devToolsExtension = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__;
	if (devToolsExtension && typeof devToolsExtension === "function") {
		composeEnhancers = devToolsExtension || compose;
	}
}

export function generateStore<State>(
	reducer: Reducer<State>,
	extraMiddleware?: Array<Middleware>
): {
	Provider: React.FC;
	withProvider: <T>(Component: ComponentType<T>) => ComponentType<T>;
	store: Store<State, any>;
	useDispatch: () => ThunkDispatch<State, any, Action>;
	useSelector: <Selected>(
		selector: (state: State) => Selected,
		equalityFn?:
			| ((previous: Selected, next: Selected) => boolean)
			| undefined
	) => Selected;
} {
	const appliedMiddleware = composeEnhancers(
		applyMiddleware(...middleWare, ...(extraMiddleware ?? []))
	);

	const store = createStore(reducer, appliedMiddleware);

	const StoreContext = React.createContext(null as any);

	const StoreProvider: React.FC = ({ children }) => {
		return (
			<Provider context={StoreContext} store={store}>
				{children}
			</Provider>
		);
	};

	function withProvider<T>(Component: ComponentType<T>): ComponentType<T> {
		return props => {
			return (
				<Provider context={StoreContext} store={store}>
					<Component {...props} />
				</Provider>
			);
		};
	}

	return {
		Provider: StoreProvider,
		withProvider,
		store,
		useDispatch: createDispatchHook<State>(StoreContext),
		useSelector: createSelectorHook<State>(StoreContext)
	};
}
